import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comicCategories } from '@/mocks/categories';
import { comics } from '@/mocks/comics';

type SortKey = 'new' | 'hot' | 'rating';

const sorts: { key: SortKey; label: string }[] = [
  { key: 'new', label: '最新' },
  { key: 'hot', label: '热度' },
  { key: 'rating', label: '评分' },
];

export default function Category() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [sortKey, setSortKey] = useState<SortKey>('new');

  const category = comicCategories.find((c) => c.slug === slug);
  const isHot = slug === 'hot';
  const isAll = slug === 'all' || !category;

  const title = category ? category.name : isHot ? '最新更新' : '全部漫画';
  const description = category
    ? category.description
    : isHot
      ? '歪歪漫画每日 10:00 与 18:00 更新的最新魔性爆笑漫画，第一时间追更不迷路。'
      : '韩国歪歪漫画恶搞反转神展开脑洞魔性爆笑漫画合集，全部作品分类浏览，轻松找到想看的入口。';

  const list = useMemo(() => {
    let result = [...comics];
    if (category) result = result.filter((c) => c.categorySlug === category.slug);
    if (q) {
      const kw = q.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.author.toLowerCase().includes(kw) ||
          c.category.includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    const viewNum = (v: string) => Number(v.replace('万', ''));
    if (sortKey === 'new') result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    if (sortKey === 'hot') result.sort((a, b) => viewNum(b.views) - viewNum(a.views));
    if (sortKey === 'rating') result.sort((a, b) => b.rating - a.rating);
    return result;
  }, [category, q, sortKey]);

  const trending = useMemo(
    () =>
      [...comics]
        .sort((a, b) => Number(b.views.replace('万', '')) - Number(a.views.replace('万', '')))
        .slice(0, 5),
    [],
  );

  const crumbs = [{ label: '首页', to: '/' }, { label: category ? '漫画分类' : isHot ? '最新更新' : '全部漫画' }];
  if (category) crumbs.push({ label: category.name });

  const collectionLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${title} - 歪歪漫画`,
    description,
    inLanguage: 'zh-CN',
    mainEntity: list.map((c) => ({
      '@type': 'CreativeWork',
      name: c.title,
      description: c.description,
    })),
  };

  return (
    <>
      <Seo
        title={`${title}合集 - 韩国歪歪漫画${category ? category.name : '恶搞反转'}爆笑漫画`}
        description={description}
        keywords={`歪歪漫画,${category ? category.name : '恶搞'},韩国歪歪漫画,爆笑漫画`}
        canonicalPath={`/category/${slug}`}
        jsonLd={[collectionLd]}
      />
      <Navbar />
      <main className="min-h-screen bg-background-100 pt-16 md:pt-18">
        <div className="container-page py-8 md:py-10">
          <Breadcrumb items={crumbs} />

          <div className="mt-6 rounded-3xl border border-background-200/70 bg-background-50 p-6 md:p-8">
            <span className="inline-flex items-center gap-1.5 text-sm font-bold text-primary-600">
              <i className="ri-book-open-line" aria-hidden="true" />
              {category ? `${category.count} 部作品` : '全站作品合集'}
            </span>
            <h1 className="mt-2 text-3xl text-foreground-950 md:text-4xl">{title}</h1>
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">{description}</p>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1">
              {sorts.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setSortKey(s.key)}
                  className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    sortKey === s.key ? 'bg-primary-500 font-bold text-background-50' : 'text-foreground-600 hover:text-foreground-950'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <p className="text-sm text-foreground-600">
              {q ? `「${q}」的搜索结果：` : ''}
              共 <strong className="text-primary-600">{list.length}</strong> 部作品
            </p>
          </div>

          {list.length === 0 ? (
            <div className="mt-10 rounded-3xl border border-dashed border-background-300 bg-background-50 p-12 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true" />
              <p className="mt-3 text-foreground-600">没有找到相关作品，换个关键词试试吧。</p>
              <Link to="/category" className="mt-4 inline-flex whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600">
                浏览全部作品
              </Link>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-4">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:col-span-3">
                {list.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>

              <aside className="lg:col-span-1">
                <div className="rounded-2xl border border-background-200/70 bg-background-50 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                    <i className="ri-grid-line text-primary-500" aria-hidden="true" />
                    其他分类
                  </h2>
                  <ul className="mt-4 flex flex-col gap-1.5">
                    {comicCategories
                      .filter((c) => c.slug !== slug)
                      .map((c) => (
                        <li key={c.slug}>
                          <Link
                            to={`/category/${c.slug}`}
                            className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                          >
                            <span className="flex items-center gap-2">
                              <i className={`${c.icon} text-primary-500`} aria-hidden="true" />
                              {c.name}
                            </span>
                            <span className="text-xs text-foreground-500">{c.count}</span>
                          </Link>
                        </li>
                      ))}
                  </ul>
                </div>

                <div className="mt-5 rounded-2xl border border-background-200/70 bg-background-50 p-5">
                  <h2 className="flex items-center gap-2 font-heading text-lg text-foreground-950">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true" />
                    本周热榜
                  </h2>
                  <ol className="mt-4 flex flex-col gap-3">
                    {trending.map((c, index) => (
                      <li key={c.id}>
                        <Link to={`/comic/${c.id}`} className="group flex items-center gap-3">
                          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${index < 3 ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-600'}`}>
                            {index + 1}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-sm text-foreground-800 group-hover:text-primary-700">{c.title}</span>
                          <span className="text-xs text-foreground-500">{c.rating}分</span>
                        </Link>
                      </li>
                    ))}
                  </ol>
                </div>
              </aside>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}